import { Component, Element, h, Host, Prop, State} from '@stencil/core';
import { HTMLTiInputElement } from '@ticom/ui-components/dist';
import { InputChangeEventDetail } from '@ticom/ui-components/dist/types/components/input/input.interface';
import { TiMetricsTracker} from '@ticom/web-component-utils';
import { default as languageStrings } from '../../global/l10n/PartNotification.json';
import { FeatureDialogService } from '../feature-dialog-launcher/feature-dialog-launcher.service';

@Component({
    tag: 'ti-part-notification',
    styleUrl: 'part-notification.scss',
    shadow: true,
})
export class PartNotification {

    /**
     * keep track of if the part is added
     *
     * @type {Boolean}
     */
    private _added: boolean;

    /**
     * The quantity field input element - for resetting on changes.
     *
     * @private
     * @type {HTMLTiInputElement}
     */
    private _inputElement: HTMLTiInputElement;

    @TiMetricsTracker()
    @Element()
    hostElement: HTMLTiPartNotificationElement;

    /**
     * Locale-specific set of language strings
     * @private
     * @type {Record<string, string>}
     */
    private _localeStrings: Record<string, string>;

    /**
     * Check if user is logged in
     *
     * @type {boolean}
     */
    private _loginStatus: boolean = true;

    /**
     * Keeps track of the notification limit
     *
     * @type {string}
     */
    private _notificationLimit: string;

    /**
     * quantity to be added to the cart
     */
    private _notificationFrequency: number = 1;

    /**
     * The OPN of the part that needs the notification
     * @type {string}
     *
     */
    private _opnInfoApiUrl: string = ``;

    /**
     * The description of the OPN.
     * We will get this from the service and populate the dialog with it.
     *
     * @type {string}
     */
    private _opnDescription: string;

    /**
     * quantity to be added to the cart
     */
    private _quantity: number = null;

    /**
     * Keep track of the quantity input status
     *
     * @type {boolean}
     */
    private _quantityError: boolean = true;

    /**
     * Validates if part should be shown to user
     * @type {boolean}
     */
    @State() availableToNotify: boolean;

    /**
     * quantity error text when allowing user to enter quantity input
     *
     * @type {string}
     */
    @State() quantityErrorText: string;

    /**
     * Keeps track of the remaining notifications
     *
     * @type {number}
     */
    @State() remainingNotifications: number;

    /**
     * The GPN of the part
     * @type {string}
     *
     */
    @Prop() gpn: string;

    /**
     * Locale, defaults to en-US
     * @type {string}
     * @default 'en-US'
     */
    @Prop() locale: string = 'en-US';

    /**
     * The OPN of the part that needs the notification
     * @type {string}
     */
    @Prop({ mutable: true }) opn!: string;

    /**
     * variable is used for the domain for the API call
     *
     */
    @Prop() siteId: string = '';

    /**
     * Fires metric event
     *
     * @param action - action taken
     * @param label  - label of object action is taken on
     * @param link   - populated if action navigates to new page
     */
    private _handleMetrics(action, label, link) {
        if (typeof (window as any)._tiAnalyticsTrack !== 'undefined') {
            (window as any)._tiAnalyticsTrack('custom event',
                {
                    'event-name': 'inventory_notifications_modal',
                    'legacy-event-category': 'inventory notification modal',
                    'legacy-event-action': action,
                    'legacy-event-label': label,
                    'data-product': 'alert-management',
                    'link-url': link
                }
            );
        }
    }

    private async _validComponentCheck() {
        this._localeStrings = languageStrings[this.locale] ?? languageStrings['en-US'] ?? {};
        this._opnInfoApiUrl = `${this.siteId}/notification-settings/api/p/opn?opn={opn}&locale={locale}`;
        this._opnInfoApiUrl = this._opnInfoApiUrl.replace('{opn}', this.opn);
        this._opnInfoApiUrl = this._opnInfoApiUrl.replace('{locale}', this.locale);
        try {
            const opnInfoFetch = await fetch(this._opnInfoApiUrl, {
                method: 'GET'
            });
            if (!opnInfoFetch.ok) {
                const err = new Error(`Received ${opnInfoFetch.status}: ${opnInfoFetch.statusText}`);
                throw err;
            }
            else {
                const json = await opnInfoFetch.json();
                this.availableToNotify = json.availableToNotify;
            }
        }
        catch (err) {
            console.log('Error: ' + err);
        }
    }

    componentWillLoad() {
        if (this.opn == null) {
            this.opn = this.gpn;
        }
    }

    async componentWillRender() {
        await this._validComponentCheck();
    }

    /**
     * Closes the dialog
     */
    private _closeDialog() {
        this._quantity = null;
        this._notificationFrequency = 1;
        FeatureDialogService.closeDialog();
        this._handleMetrics('add inventory notification', 'cancel', '');
    }

    private _notificationFrequencyChanged() {
        if (this._notificationFrequency == 1) {
            this._notificationFrequency = 0;
            this._handleMetrics('frequency selection', 'every time', '');
        } else {
            this._notificationFrequency = 1;
            this._handleMetrics('frequency selection', 'one time', '')
        }
    }

    private _checkQuantity(event): boolean {
        this._quantity = event.detail.value;
        if (event.detail.value !== '') {
            if (event.detail.value < 1) {
                this.quantityErrorText = this._localeStrings['greaterThanZeroError'];
                const form = document.getElementById('inventory-form');
                form.setAttribute('error', '');
                form.setAttribute('error-text', this.quantityErrorText);
                this._quantityError = true;
                return true;
            }
            return this._verifyQuantity(event.detail.value);
        }
        else {
            this.quantityErrorText = this._localeStrings['emptyFieldError'];
            const form = document.getElementById('inventory-form');
            form.setAttribute('error', '');
            form.setAttribute('error-text', this.quantityErrorText);
            this._quantityError = true;
            return true;
        }
    }

    private _showError() {
        this.quantityErrorText = this._localeStrings['emptyFieldError'];
        const form = document.getElementById('inventory-form');
        form.setAttribute('error', '');
        form.setAttribute('error-text', this.quantityErrorText);
    }

    /**
     * return error dialog content
     */
    private _getLoginErrorDialogContent() {
        const sanitizedCurrentLocation = encodeURIComponent(window.location.href.replace('?login-check=true', ''));
        const loginUrl = `/secure-link-forward/?gotoUrl=${sanitizedCurrentLocation}`;
        return ([
            <h2 slot="title" class="ti-add-to-cart-dialog-title">{this._localeStrings['partNotificationLabel']}</h2>,
            <p innerHTML={this._localeStrings['loggedOutStateText']}></p>,
                <ti-button id="action" slot="action" appearance="primary">
                    <a href={loginUrl} onClick={() => this._handleMetrics('add inventory notification', 'login', loginUrl)}>
                        {this._localeStrings['loginLabel']}
                    </a>
                </ti-button>,
                <ti-button slot="action" appearance="secondary" onClick={() => this._closeDialog()} >
                    {this._localeStrings['cancelLabel']}
                </ti-button>

        ]);
    }

    private async _updateStatus() {
        this._localeStrings = languageStrings[this.locale] ?? languageStrings['en-US'] ?? {};
        this._opnInfoApiUrl = `${this.siteId}/notification-settings/api/p/opn?opn={opn}&locale={locale}`;
        this._opnInfoApiUrl = this._opnInfoApiUrl.replace('{opn}', this.opn);
        this._opnInfoApiUrl = this._opnInfoApiUrl.replace('{locale}', this.locale);
        try {
            fetch(this._opnInfoApiUrl, {
                method: 'GET',
                credentials: 'include'
            }).then((response: any) => {
                if (response.ok) {
                    return response.json();
                } else {
                    return Promise.reject(new Error(`Failed to retrieve inventory alert status`));
                }
            }).then((json: {
                added: boolean,
                availableToNotify: boolean,
                inventoryLimit: number,
                login: boolean,
                notificationLimit: string,
                remainingNotifications: number,
                tiComInventory: number,
                description: string
            }) => {
                this._opnDescription = json.description;
                this._added = json.added;
                this._loginStatus = json.login;
                this._notificationLimit = json.notificationLimit;
                this.remainingNotifications = Number(json.remainingNotifications);
            }).then(() => {
                this._openNotifyMeDialog();
            }).catch((err) => {
                console.error(err);
            });
        }
        catch (err) {
            console.log('Error: ' + err);
        }
    }

    /**
     * Fetches the content for the dialog
     */
    private _fetchDialogContent() {
        this._quantity = null;
        this._notificationFrequency = 1;
        const dialog = ([
            <h2 slot="title">{this._localeStrings['partNotificationLabel']}</h2>,
            <div >
                <h3 class="u-header-4" slot="title">{this._localeStrings['partNotificationSubtitle']}</h3>
                {this.remainingNotifications <= 10 &&
                    <span class="ti_p-iconText u-margin-bottom-6" role="alert">
                        <span class="ti_p-iconText-icon"><ti-svg-icon size="s" appearance="warn">warning</ti-svg-icon></span>
                            <span>  {this._localeStrings['inventoryRemainingText'].replace('{0}', this.remainingNotifications.toString()).replace('{1}', this._notificationLimit)} </span>
                        </span>
                }

                <p class="u-header-5 u-margin-bottom-2">{this.opn}</p>
                <p>{this._opnDescription}</p>

                <ti-form-element class="u-margin-bottom-4" id="inventory-form" label-text={this._localeStrings['inventoryNeededLabel']} onClick={() => { this._handleMetrics('inventory focus', 'inventory needed', ''); }}>
                    <ti-input ref={el => this._inputElement = el}
                        pattern="[0-9]*"
                        value={this._quantity ? null : this._quantity}
                        placeholder={this._localeStrings['quantityPlaceholderText']}
                        onTiChange={(e: CustomEvent<InputChangeEventDetail>) => this._checkQuantity(e)}
                    >
                    </ti-input>

                </ti-form-element>
                <ti-form-element
                    label-text={this._localeStrings['notificationFrequencyLabel']}>
                    <ti-radio-group onClick={() => this._notificationFrequencyChanged()}>
                        <ti-radio selected={this._notificationFrequency} >{this._localeStrings['oneTimeNotificationText']}</ti-radio>
                        <ti-radio selected={!this._notificationFrequency}>{this._localeStrings['everyTimeNotificationText']}</ti-radio>
                    </ti-radio-group>
                </ti-form-element>
            </div>,
            <ti-button slot="action" appearance="primary" onClick={() => this._submitPartNotification()}>{this._localeStrings['submitLabel']}</ti-button>,
            <ti-button slot="action" appearance="secondary" onClick={() => this._closeDialog()}>{this._localeStrings['cancelLabel']}</ti-button>
        ]);
        return dialog;
    }

    /**
     * Opens the provided dialog
     * @param dialogContent - content to be opened
     */
    private _openDialog(dialogContent) {
        this._handleMetrics('open inventory notification dialog', 'part notification', '');
        // Open the dialog
        const dialogConfig = {
            class: 'ti-dialog',
            content: dialogContent,
            modal: true,
        };
        FeatureDialogService.openDialog(dialogConfig);
    }

    /**
     * Opens the provided dialog
     * @param dialogContent - content to be opened
     */
    private _openInputDialog(dialogContent) {
        this._handleMetrics('open inventory notification dialog', 'part notification', '');
        //Open the dialog
        const dialogConfig = {
            class: 'ti-dialog',
            content: dialogContent,
            modal: true,
            blocking: true,
            hideCloseButton: false,

        };
        FeatureDialogService.openDialog(dialogConfig);
    }

    /**
     * Prepares to open the NotifyMe dialog
     */
    private _openNotifyMeDialog() {
        // Check if user is logged in
        if (this._loginStatus) {
            // modal if user has reachd max number of notifications
            const maxNotificationsdialog = ([
                <h2 slot="title">{this._localeStrings['unableToAddLabel']}</h2>,
                <div>
                    <p>{this._localeStrings['maxNotificationsText'].replace('{0}', this._notificationLimit)}</p>
                </div>,
                <ti-button slot="action" appearance="primary" onClick={() => this._closeDialog()}>{this._localeStrings['okLabel']}</ti-button>,
                <ti-button slot="action" appearance="secondary">
                    <a href={`${this.siteId}/notification-settings/inventory-notifications`} onClick={() => { this._handleMetrics('add inventory notification', 'manage notifications', `${this.siteId}/notification-settings/inventory-notifications`) }}>
                        {this._localeStrings['manageNotificationsLabel']}
                    </a>
                </ti-button>]);
            // modal if user has already added part notification
            const alreadySubscribedDialog = ([
                <h2 slot="title">{this._localeStrings['partNotificationLabel']}</h2>,
                <div>
                    <p>{this._localeStrings['alreadySubscribedText']}</p>
                </div>,
                <ti-button slot="action" appearance="primary" >
                    <a href={`${this.siteId}/notification-settings/inventory-notifications`} onClick={() => { this._handleMetrics('add inventory notification', 'view notifications', `${this.siteId}/notification-settings/inventory-notifications`)}}>
                        {this._localeStrings['viewNotificationsLabel']}
                    </a>
                </ti-button>,
                <ti-button slot="action" appearance="secondary" onClick={() => this._closeDialog()} >{this._localeStrings['cancelLabel']}</ti-button>,
            ]);
            if (this._added)
                this._openDialog(alreadySubscribedDialog);
            else if (this.remainingNotifications <= 0)
                this._openDialog(maxNotificationsdialog);
            else {
                const dialogContent = this._fetchDialogContent();
                this._openInputDialog(dialogContent);
            }
        }
        else {
            const dialogContent = this._getLoginErrorDialogContent();
            this._openDialog(dialogContent);
        }
    }

    /**
     * Submits the quantity/email request to the endpoint, if they are valid
     */
    private async _submitPartNotification() {
        if (this._quantity == null) {
            this._showError();
            return;
        }
        if (this._quantityError == true) {
            return;
        }
        fetch(`${this.siteId}/notification-settings/api/inventory-alerts/insertInventoryData`, {
            method: 'POST',
            body: JSON.stringify({
                orderablePartNumber: this.opn,
                quantityRequested: this._quantity,
                subscriptionType: this._notificationFrequency == 1 ? 'ONE_TIME' : 'EVERY_TIME'
            }),
            headers: {
                'Content-Type': 'application/json'
            }
        }).then(async (response) => {
            if (response.ok) {
                this._closeDialog();
                this._handleMetrics('add inventory notification', this.opn, '');
            }
            return response;
        }).then((response) => {
            if (response.ok) {
                const snackbarService = (window as any)?.com?.TI.SnackbarService ?? null;
                snackbarService?.showImmediately(this._localeStrings['confirmationText'].replace('{0}', this.opn), this._localeStrings['viewNotificationsLabel'], '/notification-settings/inventory-notifications');
            }
        }).catch((error) => {
            console.error(error);
        });

    }

    private _verifyQuantity(newQty?: number) {
        let hasError = false;
        if (newQty === undefined) {
            this.quantityErrorText = this._localeStrings['emptyFieldError'];
            hasError = true;
            this._quantityError = true;
        }
        //validate newQty if it's defined
        if (newQty !== undefined) {
            if (newQty > 999999999) {
                this.quantityErrorText = this._localeStrings['lessThanMaxError'];
                this._quantity = newQty;
                const form = document.getElementById('inventory-form');
                form.setAttribute('error', '');
                form.setAttribute('error-text', this.quantityErrorText);
                hasError = true;
                this._quantityError = true;
            }
            else {
                this._quantity = newQty;
                const form = document.getElementById('inventory-form');
                form.setAttribute('error', '');
                form.setAttribute('error-text', '');
                if (this._inputElement && this._quantity) {
                    this._inputElement.value = this._quantity;
                }
                this._quantityError = false;
                hasError = false;
            }
        }
        this._quantity = newQty;
        return hasError;
    }

    /**
     * Renders the component
     */
    render() {
        if (this.availableToNotify) {
            return (
                <Host>
                    <ti-button appearance="link" blocking onClick={() => this._updateStatus()}>
                        <span class="ti-notify-me-svg-icon"><ti-svg-icon size="s" appearance="secondary" icon-set="actions">alarm</ti-svg-icon></span>
                        <span>{this._localeStrings['partNotificationLabel']}</span>
                    </ti-button>
                </Host>
            );
        }
        else {
            return null;
        }
    }
}
